import { List, Map, Record } from 'immutable';
import I18n from '../lib/i18n';
import { ITopicRecord } from './Topic';

import { difference } from 'lodash';

interface IRecord {
  id?: number;
  available_countries?: string[];
  available_countries_nintendo_switch_2?: string[];
  available_languages?: string[];
  has_aocs?: boolean;
  has_tickets?: boolean;
  has_consumables?: boolean;
  name?: string;
  ns_uid: string;
  application_id?: string;
  oldest_release_at?: string;
  platforms?: string[];
  images?: { [s: string]: number };
  product_names: { [s: string]: string };
  related_channels?: ITopicRecord[];
  errors?: Map<string, string>;
  valid: boolean;
}
const defaultValue: IRecord = {
  id: 0,
  available_countries: [],
  available_countries_nintendo_switch_2: [],
  available_languages: [],
  has_aocs: false,
  has_tickets: false,
  has_consumables: false,
  name: '',
  ns_uid: '',
  application_id: '',
  oldest_release_at: '',
  platforms: [],
  images: {},
  product_names: {},
  related_channels: [],
  errors: Map(),
  valid: true,
};

export default class Product extends Record(defaultValue) {
  public static maxLength = 80;

  // convinience read methods
  public static aocs(product: Product): boolean {
    if (product.has_aocs) { return true; }
    return false;
  }

  public static isSoftDetail(nsUid: string): boolean {
    return (nsUid === undefined) ? false : nsUid.startsWith('7001');
  }

  public static isAocDetail(nsUid: string): boolean {
    return (nsUid === undefined) ? false : nsUid.startsWith('7005');
  }

  public static isBundleDetail(nsUid: string): boolean {
    return (nsUid === undefined) ? false : nsUid.startsWith('7007');
  }

  public static isPreticketDetail(nsUid: string): boolean {
    return (nsUid === undefined) ? false : nsUid.startsWith('7002');
  }

  public static buildCountrySearchQuery(countries: string[]): string {
    return countries.map((country: string) => `q[country_products_country_in][]=${country}`).join('&');
  }
  constructor(product: IRecord) {
    const { ...another } = product;
    const errors = product.errors === undefined ? {} : product.errors;
    super({
      errors: Map(errors),
      ...another,
    });
  }

  public availableName(): string {
    if (!this.valid) { return `${this.ns_uid}: ${I18n.t('products.index.invalid_product')}`; }
    const availableLanguages = this.available_languages === undefined ? '' : this.available_languages.join(',');
    return `[${this.application_id}(${this.ns_uid})] ${this.name}(${availableLanguages})`;
  }

  public toSelectOption(): { label: string, value: string, names?: { [s: string]: string }} {
    return {
      label: this.availableName(),
      value: this.ns_uid,
      names: this.product_names,
    };
  }

  public hasImagesForAllLanguages(editableLanguages: List<string>): boolean {
    return editableLanguages.filter(lang => this.images === undefined || this.images[lang] === undefined).size === 0;
  }

  public isValidAvailableCountries(countries: string[], platforms: string[]): boolean {
    const availableCountries = this.available_countries || [];
    const availableCountriesNintendoSwitch2 = this.available_countries_nintendo_switch_2 || [];

    if (platforms.length === 2) {
      // 選択しているプラットフォームが Switch かつ Switch2 の場合
      return difference(countries, availableCountries).length === 0 && difference(countries, availableCountriesNintendoSwitch2).length === 0;
    } else if (platforms[0] !== 'nx') {
      // 選択しているプラットフォームが Switch2 のみの場合
      return difference(countries, availableCountriesNintendoSwitch2).length === 0;
    } else {
      // 選択しているプラットフォームが Switch のみ または チェックなしの場合
      // チェックなしの場合は以前と同様に availableCountries のみで判定する
      return difference(countries, availableCountries).length === 0;
    }
  }
}
